/* ─────────────────────────────────────────────────────────────
   LP Fera Reviews — behaviour
   Reads config from data attributes on [data-fera-reviews-grid]:
     data-min-rating       minimum star rating to include
     data-max-reviews      max cards to render
     data-keyword-filter   comma-separated keywords (optional)
     data-display-mode     all | photos_first | mixed
───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ── helpers ── */

  function hasPhoto(review) {
    var mediaList = review.get ? review.get('media') : (review.media || []);
    return !!(mediaList && mediaList.length);
  }

  function getPhotoUrl(review) {
    var mediaList = review.get ? review.get('media') : (review.media || []);
    var media = (mediaList && mediaList.length) ? mediaList[0] : null;
    if (!media) return null;
    if (media.type === 'photo' || !media.type) return media.thumbnail_url || media.url || null;
    if (media.type === 'video')                return media.thumbnail_url || null;
    return null;
  }

  function buildCard(review) {
    var rating   = review.rating || 5;
    var title    = review.get ? review.get('heading') : (review.heading || '');
    var body     = review.get ? review.get('body')    : (review.body    || '');
    var customer = review.customer;
    var name     = customer
      ? (customer.get ? customer.get('display_name') : (customer.display_name || ''))
      : '';
    name = name || 'Verified';

    var photoUrl  = getPhotoUrl(review);
    var createdAt = review.get ? review.get('created_at') : review.created_at;
    var dateStr   = createdAt
      ? new Date(createdAt).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';

    var stars = '';
    for (var i = 1; i <= 5; i++) {
      stars += '<span class="lp-fera-reviews__card-star'
        + (i <= rating ? ' lp-fera-reviews__card-star--filled' : '')
        + '" aria-hidden="true">★</span>';
    }

    var photoHTML = photoUrl
      ? '<img class="lp-fera-reviews__card-photo" src="' + photoUrl + '" alt="Customer photo" loading="lazy">'
      : '';

    // Long bodies get clamped with a "Read more" toggle.
    var LONG_THRESHOLD = 180;
    var isLong = body && body.length > LONG_THRESHOLD;
    var bodyHTML = body
      ? '<p class="lp-fera-reviews__card-body' + (isLong ? ' lp-fera-reviews__card-body--clamped' : '') + '">' + body + '</p>'
        + (isLong ? '<button type="button" class="lp-fera-reviews__card-readmore" aria-expanded="false">Read more</button>' : '')
      : '';

    return '<div class="lp-fera-reviews__card">'
      + '<div class="lp-fera-reviews__card-stars" aria-label="' + rating + ' out of 5 stars">' + stars + '</div>'
      + (title ? '<p class="lp-fera-reviews__card-title">' + title + '</p>' : '')
      + bodyHTML
      + photoHTML
      + '<div class="lp-fera-reviews__card-footer">'
      +   '<p class="lp-fera-reviews__card-name">' + name + '</p>'
      +   '<div class="lp-fera-reviews__card-meta"><span></span><span>' + dateStr + '</span></div>'
      + '</div>'
      + '</div>';
  }

  /* ── display-mode ordering ── */

  /**
   * all          — most recent first (default)
   * photos_first — photo reviews first (by date), then text-only (by date)
   * mixed        — interleave 1 photo : 2 non-photo, repeating
   */
  function arrangeReviews(reviews, mode) {
    if (mode === 'photos_first') {
      var withPhoto = reviews.filter(hasPhoto);
      var noPhoto   = reviews.filter(function (r) { return !hasPhoto(r); });
      return withPhoto.concat(noPhoto);
    }

    if (mode === 'mixed') {
      var withPhoto = reviews.filter(hasPhoto);
      var noPhoto   = reviews.filter(function (r) { return !hasPhoto(r); });
      var result = [];
      var pi = 0, ni = 0;
      while (pi < withPhoto.length || ni < noPhoto.length) {
        // 1 photo …
        if (pi < withPhoto.length) result.push(withPhoto[pi++]);
        // … then 2 non-photo
        if (ni < noPhoto.length)   result.push(noPhoto[ni++]);
        if (ni < noPhoto.length)   result.push(noPhoto[ni++]);
      }
      return result;
    }

    // 'all' — already sorted by date desc by the caller
    return reviews;
  }

  /* ── per-grid init ── */

  function initGrid(grid) {
    var MIN_RATING   = parseInt(grid.dataset.minRating,  10) || 4;
    var MAX_REVIEWS  = parseInt(grid.dataset.maxReviews, 10) || 12;
    var KEYWORD_RAW  = grid.dataset.keywordFilter || '';
    var DISPLAY_MODE = grid.dataset.displayMode   || 'all';

    var keywords = KEYWORD_RAW
      ? KEYWORD_RAW.toLowerCase().split(',').map(function (k) { return k.trim(); }).filter(Boolean)
      : [];

    function matchesKeywords(review) {
      if (!keywords.length) return true;
      var heading = review.get ? review.get('heading') : (review.heading || '');
      var body    = review.get ? review.get('body')    : (review.body    || '');
      var text    = ((body || '') + ' ' + (heading || '')).toLowerCase();
      return keywords.some(function (k) { return text.indexOf(k) !== -1; });
    }

    function renderReviews(reviews) {
      // Sort all incoming reviews by date desc first.
      // Fera model objects expose date via .get() — fall back to direct prop.
      var sorted = reviews.slice().sort(function (a, b) {
        var dateA = a.get ? a.get('created_at') : a.created_at;
        var dateB = b.get ? b.get('created_at') : b.created_at;
        return new Date(dateB || 0) - new Date(dateA || 0);
      });

      var filtered = sorted.filter(function (r) {
        return r.rating >= MIN_RATING && matchesKeywords(r);
      });

      var arranged = arrangeReviews(filtered, DISPLAY_MODE).slice(0, MAX_REVIEWS);

      if (!arranged.length) {
        grid.innerHTML = '<p class="lp-fera-reviews__loading">No reviews found.</p>';
        return;
      }

      grid.innerHTML = arranged.map(buildCard).join('');
    }

    // Toggle long reviews open/closed (event delegation).
    grid.addEventListener('click', function (e) {
      var btn = e.target.closest('.lp-fera-reviews__card-readmore');
      if (!btn) return;
      var body = btn.previousElementSibling;
      if (!body) return;
      var expanded = body.classList.toggle('lp-fera-reviews__card-body--expanded');
      body.classList.toggle('lp-fera-reviews__card-body--clamped', !expanded);
      btn.textContent = expanded ? 'Read less' : 'Read more';
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    function loadReviews() {
      if (typeof fera === 'undefined' || !fera.api) return;
      fera.api
        .listReviews({ per_page: 100, min_rating: MIN_RATING, sort: 'created_at', sort_order: 'desc' })
        .then(function (result) {
          renderReviews(result.reviews || []);
        })
        .catch(function (err) {
          grid.innerHTML = '<p class="lp-fera-reviews__loading">Could not load reviews.</p>';
          console.error('Fera reviews error:', err);
        });
    }

    // Use a short poll to check for fera + fera.api being ready.
    // This is more reliable than fera:ready (which may fire before this
    // deferred script runs) or a direct call (fera.api may exist but not
    // be authenticated yet). Poll stops after ~6s as a safety cap.
    var attempts = 0;
    var poll = setInterval(function () {
      if (typeof fera !== 'undefined' && fera.api) {
        clearInterval(poll);
        loadReviews();
      }
      if (++attempts > 20) clearInterval(poll);
    }, 300);
  }

  /* ── boot: init every grid on the page ── */
  document.querySelectorAll('[data-fera-reviews-grid]').forEach(initGrid);
})();
